import Resizable from '@corvu/resizable';
import { useFxWorkspaceContext } from '@mobile-mapping/providers';
import {
  Alert,
  BtnClasses,
  Button,
  Empty,
  Icon,
  IconButton,
  Spinner,
} from '@tcweb/connect-ui';
import { t } from '@trimble/i18n';
import {
  DropdownSelect,
  PanelLayoutWithHeaderToggle,
} from '@trimble/ui-components/solidjs';
import { JSX, Show, createEffect, createMemo, createSignal } from 'solid-js';

import { CreateFxWorkspaceDialog } from '../selections/tree/CreateFxWorkspaceDialog';
import { DeleteFxWorkspaceDialog } from '../selections/tree/DeleteFxWorkspaceDialog';
import {
  FX_REFERENCES_FILTER_OPTIONS,
  type FxReferencesFilterMode,
} from '../selections/tree/filter-fx-reference-nodes';
import type { FxReferencesViewMode } from '../selections/tree/flatten-fx-reference-nodes';
import { FxWorkspacesSelectionTreeContainer } from '../selections/tree/FxWorkspacesSelectionTreeContainer';
import { useActiveFxWorkspaceTree } from '../selections/tree/useActiveFxWorkspaceTree';
import { DraftLayersPanel } from './DraftLayersPanel';
import { WorkspaceEmptyReferences } from './WorkspaceEmptyReferences';

// DropDownMenu uses min-width:0 and CustomTooltip is inline-block. Without a
// real flex item (not display:contents) the triggers collapse and overlap.
const fxWorkspaceDropdownSlotClass =
  'relative min-w-0 flex-1 [&_.dropdown-pane-container]:w-full [&_.dropdown-pane-container]:max-w-full [&_.dropdown-pane-container>div]:w-full [&_.dropdown-pane-container>div]:max-w-full [&_button]:w-full [&_button]:max-w-full';

export const WorkspacesTab = (): JSX.Element => {
  const fxWorkspace = useFxWorkspaceContext();
  const [referencesFilterMode, setReferencesFilterMode] =
    createSignal<FxReferencesFilterMode>('workspace');
  const [referencesViewMode, setReferencesViewMode] =
    createSignal<FxReferencesViewMode>('tree');
  const { activeWorkspace, nodes, childrenLoadStatus } =
    useActiveFxWorkspaceTree({
      referencesFilterMode,
      referencesViewMode,
    });
  const [isCreateDialogOpen, setIsCreateDialogOpen] = createSignal(false);
  const [workspacePendingDelete, setWorkspacePendingDelete] = createSignal<
    { id: string; name: string } | undefined
  >(undefined);

  const workspaceOptions = createMemo(() =>
    fxWorkspace.workspaces().map((workspace) => ({
      key: workspace.id,
      label: workspace.name,
    }))
  );

  createEffect(() => {
    if (fxWorkspace.loadStatus() !== 'success') {
      return;
    }

    const workspaces = fxWorkspace.workspaces();
    if (workspaces.length === 0) {
      return;
    }

    if (fxWorkspace.activeWorkspaceId() === undefined) {
      fxWorkspace.activateWorkspace(workspaces[0].id);
    }
  });

  const hasWorkspaces = createMemo(() => fxWorkspace.workspaces().length > 0);

  const isError = createMemo(() => childrenLoadStatus() === 'error');

  const isLoading = createMemo(() => {
    if (isError()) {
      return false;
    }

    if (childrenLoadStatus() === 'pending') {
      return true;
    }

    // First-workspace activation runs in an effect after the list succeeds.
    // Until it commits, keep the spinner so we don't flash the empty state.
    return hasWorkspaces() && activeWorkspace() === undefined;
  });

  const canDeleteActiveWorkspace = createMemo(
    () => activeWorkspace() !== undefined
  );

  const hasWorkspaceReferences = createMemo(() => {
    const workspaceId = fxWorkspace.activeWorkspaceId();
    return (
      workspaceId !== undefined &&
      fxWorkspace.workspaceReferenceCount(workspaceId) > 0
    );
  });

  const openCreateDialog = (): void => {
    setIsCreateDialogOpen(true);
  };

  const closeCreateDialog = (): void => {
    setIsCreateDialogOpen(false);
  };

  const openDeleteDialog = (): void => {
    const workspace = activeWorkspace();
    if (workspace === undefined) {
      return;
    }

    setWorkspacePendingDelete({
      id: workspace.id,
      name: workspace.name,
    });
  };

  const closeDeleteDialog = (): void => {
    setWorkspacePendingDelete(undefined);
  };

  const handleActiveWorkspaceChange = (value: string | null): void => {
    if (value) {
      fxWorkspace.activateWorkspace(value);
    }
  };

  const handleReferencesFilterChange = (
    value: FxReferencesFilterMode | null
  ): void => {
    if (value === 'active' || value === 'workspace') {
      setReferencesFilterMode(value);
    }
  };

  const handleReferencesViewToggle = (): void => {
    setReferencesViewMode((mode) => (mode === 'tree' ? 'flat' : 'tree'));
  };

  const referencesViewToggleIcon = createMemo(() =>
    referencesViewMode() === 'tree'
      ? 'tc-icon-view-list'
      : 'tc-icon-folder-tree'
  );

  const referencesViewToggleTitle = createMemo(() =>
    referencesViewMode() === 'tree'
      ? t('mobileMapping.fxWorkspace.references.viewFlat')
      : t('mobileMapping.fxWorkspace.references.viewTree')
  );

  return (
    <div data-testid="workspaces-tab" class="contents">
      <PanelLayoutWithHeaderToggle
        panelTitle={t('mobileMapping.fxWorkspace.title')}
        titleSectionRight={
          <Show when={hasWorkspaces()}>
            <div class="flex w-full min-w-0 items-center gap-2">
              <div
                class={fxWorkspaceDropdownSlotClass}
                data-testid="active-workspace-dropdown"
              >
                <DropdownSelect
                  options={workspaceOptions()}
                  value={fxWorkspace.activeWorkspaceId() ?? ''}
                  onValueChange={handleActiveWorkspaceChange}
                />
              </div>
              <div
                class={fxWorkspaceDropdownSlotClass}
                data-testid="fx-references-filter-dropdown"
                aria-label={t(
                  'mobileMapping.fxWorkspace.references.filter.label'
                )}
              >
                <DropdownSelect
                  options={FX_REFERENCES_FILTER_OPTIONS()}
                  value={referencesFilterMode()}
                  onValueChange={handleReferencesFilterChange}
                />
              </div>
            </div>
          </Show>
        }
        rightActions={
          <>
            <Show when={hasWorkspaces()}>
              <IconButton
                color={BtnClasses.TERTIARY}
                icon={referencesViewToggleIcon()}
                size="icon-medium"
                data-testid="fx-references-view-toggle"
                title={referencesViewToggleTitle()}
                clicked={handleReferencesViewToggle}
              />
            </Show>
            <Show when={hasWorkspaceReferences()}>
              <IconButton
                color={BtnClasses.TERTIARY}
                icon="tc-icon-edit"
                size="icon-medium"
                data-testid="modify-workspace-data-button"
                title={t('mobileMapping.fxWorkspace.modifyWorkspaceData')}
                clicked={() => fxWorkspace.enterWorkspaceDataEditing()}
              />
            </Show>
            <IconButton
              color={BtnClasses.TERTIARY}
              icon="tc-icon-content-add"
              size="icon-medium"
              data-testid="create-workspace-button"
              title={t('mobileMapping.fxWorkspace.createWorkspace')}
              clicked={openCreateDialog}
            />
            <IconButton
              color={BtnClasses.TERTIARY}
              icon="tc-icon-delete"
              size="icon-medium"
              data-testid="delete-workspace-button"
              title={t('mobileMapping.fxWorkspace.delete')}
              disabled={!canDeleteActiveWorkspace()}
              clicked={openDeleteDialog}
            />
          </>
        }
        content={
          <Show
            when={!isError()}
            fallback={
              <Alert type="error">
                {t('mobileMapping.fxWorkspace.loadFailed')}
              </Alert>
            }
          >
            <Show
              when={!isLoading()}
              fallback={
                <div class="h-[300px]">
                  <Spinner show={true} />
                </div>
              }
            >
              <Show
                when={hasWorkspaces()}
                fallback={
                  <Empty
                    icon="tc-icon-folder"
                    title={t('mobileMapping.fxWorkspace.title')}
                    message={t('mobileMapping.fxWorkspace.noWorkspaces')}
                    class="flex-1 w-100"
                    data-testid="workspaces-no-data-found"
                    isSmallContainer={true}
                    content={
                      <Button
                        color={BtnClasses.PRIMARY}
                        data-testid="create-workspace-empty-button"
                        onClick={openCreateDialog}
                      >
                        {t('mobileMapping.fxWorkspace.createWorkspace')}
                      </Button>
                    }
                  />
                }
              >
                <Show when={activeWorkspace()}>
                  {(workspace) => (
                    <div class="h-full min-h-0">
                      <Resizable orientation="vertical" class="h-full w-full">
                        <Resizable.Panel
                          initialSize={0.55}
                          minSize={0.4}
                          class="min-h-0 overflow-auto"
                        >
                          <Show
                            when={hasWorkspaceReferences()}
                            fallback={
                              <WorkspaceEmptyReferences
                                onAddData={() =>
                                  fxWorkspace.enterWorkspaceDataEditing()
                                }
                              />
                            }
                          >
                            <FxWorkspacesSelectionTreeContainer
                              nodes={nodes()}
                              childrenLoadStatus={childrenLoadStatus()}
                              filterKey={workspace().id}
                              sortBy="label"
                              sortDirection="asc"
                            />
                          </Show>
                        </Resizable.Panel>
                        <Resizable.Handle
                          aria-label={t(
                            'mobileMapping.fxWorkspace.draftLayers.resizeHandle'
                          )}
                          class="group basis-4 flex items-center justify-center cursor-row-resize select-none text-icon-secondary hover:bg-base-200 corvu-group-active:bg-base-200 corvu-group-dragging:bg-base-200 transition-colors"
                        >
                          <Icon icon="tc-icon-drag-horiz" size="icon-small" />
                        </Resizable.Handle>
                        <Resizable.Panel
                          initialSize={0.45}
                          minSize={0.15}
                          class="min-h-0 overflow-hidden"
                        >
                          <DraftLayersPanel workspaceId={workspace().id} />
                        </Resizable.Panel>
                      </Resizable>
                    </div>
                  )}
                </Show>
              </Show>
            </Show>
          </Show>
        }
      />
      <Show when={isCreateDialogOpen()}>
        <CreateFxWorkspaceDialog
          show={isCreateDialogOpen()}
          onClose={closeCreateDialog}
        />
      </Show>
      <Show when={workspacePendingDelete()}>
        {(workspace) => (
          <DeleteFxWorkspaceDialog
            workspaceId={workspace().id}
            workspaceName={workspace().name}
            onClose={closeDeleteDialog}
          />
        )}
      </Show>
    </div>
  );
};
